/**
 * VolgendeKeerKaart: "Neem een kleed-abonnement" (plan 2026-10-26; sinds 27-10
 * alleen het abonnement, besluit Willem: één aanbod, één vraag).
 *
 * De kaart stelt één vraag: hoe vaak? Vier ritmes (elk half jaar, elk jaar,
 * elke 1,5 jaar, elke 2 jaar), 10% korting, drie weken vooraf bericht,
 * overslaan mag, stoppen kan altijd (A01 als bevestiging). Het startseizoen
 * volgt standaard het moment van deze reiniging (basis + interval, server:
 * ritmeStartOpties); "Ander seizoen?" opent de buren. "Ik beslis zelf" is een
 * tekstlink: die klant blijft gewoon in de verjaardagmail. Geen vaste
 * reinigingstermijn als advies (kennisbankregel): de klant kiest.
 *
 * De losse herinnering (terugkom-moment) is uit het aanbod, niet uit het
 * systeem: kantoor en de mailagent zetten hem op verzoek (zet_terugkom_klant)
 * en een bestaande herinnering toont de kaart gewoon als stand.
 *
 * Staat bovenaan de afgerond-view, in de aflever-fase zodra er niets meer te
 * betalen is, op ?type=terugkom (links uit S10, de care-guide en WhatsApp, met
 * het ritme voorgekozen via ?interval=) en op Mijn Kleda. Een klik in de mail
 * legt niets vast: hier bevestigt de klant met één knop. Oudere mails (tot
 * 27-10) gaven ?kies=<seizoen> mee; dat seizoen wordt het startseizoen van het
 * ritme waarin het past.
 *
 * De stand komt uit de server (actief abonnement of open herinnering), zodat
 * de kaart na een herlaad hetzelfde toont. Kanaalkeuze (mail of WhatsApp)
 * achter VITE_KANAALKEUZE_ENABLED (golf 3). Zonder VITE_KLEDA_ABONNEMENT_ENABLED
 * valt er niets aan te bieden: de kaart verdwijnt (rollback = geen kaart).
 */
import { useEffect, useState } from 'react'
import {
  fetchVolgendeReiniging,
  maakKleedAbonnement,
  zetTerugkom,
  type AbonnementStand,
  type RitmeStartOptie,
  type VolgendeKeerBron,
} from '../api'
import {
  trackAbonnementConfirmed,
  trackAbonnementViewed,
  trackTerugkomDeclined,
  trackTerugkomViewed,
} from '../analytics'
import { Card } from './UI'

const KANAALKEUZE_AAN = import.meta.env.VITE_KANAALKEUZE_ENABLED === 'true'
const ABONNEMENT_AAN = import.meta.env.VITE_KLEDA_ABONNEMENT_ENABLED === 'true'
const MIJN_KLEDA_AAN = import.meta.env.VITE_MIJN_KLEDA_ENABLED === 'true'
const INTERVALLEN = [6, 12, 18, 24] as const
const VOORWAARDEN = 'https://kleda.nl/kleed-abonnement'

/** Knoptekst per ritme; dezelfde woorden als de S10-chips (office: volgende-reiniging.ts). */
const RITME_KNOP: Record<number, string> = { 6: 'Elk half jaar', 12: 'Elk jaar', 18: 'Elke 1,5 jaar', 24: 'Elke 2 jaar' }
/** Hetzelfde ritme in een zin: "daarna elk jaar". */
const RITME_ZIN: Record<number, string> = { 6: 'elk half jaar', 12: 'elk jaar', 18: 'elke anderhalf jaar', 24: 'elke twee jaar' }
function ritmeKnop(m: number): string {
  return RITME_KNOP[m] ?? `Elke ${m} maanden`
}
export function ritmeZin(m: number): string {
  return RITME_ZIN[m] ?? `elke ${m} maanden`
}

interface Props {
  token: string
  werkOrderId: string
  heeftMobiel?: boolean
  bron?: VolgendeKeerBron
  /** Uit de URL (?interval=12, oudere links ?kies=<seizoen>): de keuze uit de mail staat al klaar. */
  voorkeuze?: { seizoen?: string | null; abonnement?: boolean; interval?: number | null }
  /** Losse pagina (?type=terugkom): toon een korte uitleg als er niets te kiezen valt. */
  standalone?: boolean
  /** Op Mijn Kleda zelf geen link naar Mijn Kleda. */
  verbergMijnKledaLink?: boolean
  /** Standalone-tekst als er niets te kiezen valt (Mijn Kleda wijst dan naar "Bestel opnieuw"). */
  leegTekst?: string
  className?: string
}

function kapitaal(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function datumNl(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`)
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : iso
}

/** "in het najaar van 2027", "in de winter van 2027/2028", "in maart 2027". */
function inSeizoen(label: string): string {
  const m = /^(voorjaar|zomer|najaar|winter) (\d{4}(?:\/\d{4})?)$/i.exec(label.trim())
  if (m) {
    const naam = m[1].toLowerCase()
    const lidwoord = naam === 'winter' || naam === 'zomer' ? 'de' : 'het'
    return `in ${lidwoord} ${naam} van ${m[2]}`
  }
  return `in ${label}`
}

export function mijnKledaHref(token: string): string | null {
  if (!MIJN_KLEDA_AAN) return null
  return `/?token=${encodeURIComponent(token)}&type=mijn-kleda`
}

export function VolgendeKeerKaart({
  token, werkOrderId, heeftMobiel = false, bron = 'client-app', voorkeuze, standalone = false, verbergMijnKledaLink = false, leegTekst, className = 'mt-md',
}: Props) {
  const [geladen, setGeladen] = useState(false)
  const [toegestaan, setToegestaan] = useState(false)
  const [ritmeOpties, setRitmeOpties] = useState<Record<string, RitmeStartOptie[]>>({})
  const [heeftSeizoenen, setHeeftSeizoenen] = useState(false)
  const [interval, setInterval_] = useState<number | null>(null)
  const [startKeuze, setStartKeuze] = useState<string | null>(null)
  const [seizoenOpen, setSeizoenOpen] = useState(false)
  const [gekozen, setGekozen] = useState<{ seizoen: string | null; vanaf: string } | null>(null)
  const [abonnement, setAbonnement] = useState<AbonnementStand | null>(null)
  const [kanaal, setKanaal] = useState<'mail' | 'whatsapp'>('mail')
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)
  const [zelf, setZelf] = useState(false)
  const [netBevestigd, setNetBevestigd] = useState(false)

  // Ritme kiezen zet het startseizoen op de standaard (het seizoen waarin
  // basis + interval valt); "Ander seizoen?" opent de buren.
  const kiesInterval = (m: number, tabel: Record<string, RitmeStartOptie[]> = ritmeOpties) => {
    setInterval_(m)
    setSeizoenOpen(false)
    const lijst = tabel[String(m)] ?? []
    setStartKeuze(lijst.find((o) => o.standaard)?.label ?? lijst[0]?.label ?? null)
  }

  useEffect(() => {
    let actief = true
    fetchVolgendeReiniging(token, bron).then((r) => {
      if (!actief) return
      setGeladen(true)
      if (!r.success || !r.data) return
      const d = r.data
      const tabel = d.ritmeOpties ?? {}
      setToegestaan(d.toegestaan ?? d.afgeleverd)
      setRitmeOpties(tabel)
      setHeeftSeizoenen(d.opties.length > 0)
      if (d.gekozen) setGekozen({ seizoen: d.gekozen.seizoen, vanaf: d.gekozen.vanaf })
      if (d.abonnement) setAbonnement(d.abonnement)
      // Voorkeuze uit de mail: ?interval=12. Oudere links (?kies=<seizoen>) landen
      // op het ritme waarin dat seizoen als start past.
      const vi = voorkeuze?.interval
      if (vi && (INTERVALLEN as readonly number[]).includes(vi)) {
        kiesInterval(vi, tabel)
      } else {
        const voor = voorkeuze?.seizoen?.trim().toLowerCase()
        const treffer = voor ? INTERVALLEN.find((m) => (tabel[String(m)] ?? []).some((o) => o.label === voor)) : undefined
        if (voor && treffer) {
          setInterval_(treffer)
          setStartKeuze(voor)
        }
      }
      trackTerugkomViewed(werkOrderId, Object.values(tabel).filter((l) => l.length > 0).length, bron)
      if (voorkeuze?.abonnement || vi) trackAbonnementViewed(werkOrderId, bron)
    })
    return () => { actief = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, werkOrderId, bron])

  if (!geladen) return null
  const mkHref = verbergMijnKledaLink ? null : mijnKledaHref(token)
  const mijnKledaLink = mkHref ? (
    <a href={mkHref} className="mt-sm inline-block text-body-sm font-medium text-primary-dark underline">Wijzigen op Mijn Kleda</a>
  ) : null

  // ── Stand: kleed-abonnement ───────────────────────────────────
  if (abonnement) {
    return (
      <Card className={className}>
        <p className="text-body font-medium text-ink mb-xs">
          {netBevestigd ? 'Je abonnement staat' : `Je kleed-abonnement: ${ritmeZin(abonnement.intervalMaanden)}`}
        </p>
        <p className="text-body-sm text-ink-muted">
          Je volgende reiniging staat {abonnement.seizoenLabel ? inSeizoen(abonnement.seizoenLabel) : `rond ${datumNl(abonnement.nextRunAt)}`}, met {abonnement.kortingPct}% korting.
          Drie weken vooraf krijg je een berichtje; tot en met {datumNl(abonnement.overslaanTot)} kun je overslaan of stoppen.
          {netBevestigd ? ' Je bevestiging komt per mail.' : ''}
        </p>
        {mijnKledaLink}
      </Card>
    )
  }

  // ── Stand: herinnering (gezet door kantoor, de mailagent of vóór 27-10) ──
  if (gekozen) {
    const wis = async () => {
      setBezig(true)
      const r = await zetTerugkom({ token, actie: 'wissen', bron })
      setBezig(false)
      if (r.success) setGekozen(null)
    }
    return (
      <Card className={className}>
        <p className="text-body font-medium text-ink mb-xs">
          Volgende reiniging: {gekozen.seizoen ?? datumNl(gekozen.vanaf)}
        </p>
        <p className="text-body-sm text-ink-muted">
          We sturen je dan een offerte met de prijs van dat moment. Je zit nergens aan vast.
        </p>
        <div className="mt-sm flex flex-wrap items-center gap-md">
          {mkHref && <a href={mkHref} className="text-body-sm font-medium text-primary-dark underline">Wijzigen op Mijn Kleda</a>}
          <button type="button" disabled={bezig} onClick={() => void wis()} className="text-body-sm text-ink-muted underline disabled:opacity-50">
            Toch liever zelf beslissen
          </button>
        </div>
      </Card>
    )
  }

  if (zelf) {
    return (
      <Card className={className}>
        <p className="text-body-sm text-ink-muted">
          Prima, je beslist zelf. Een nieuwe offerte aanvragen kan altijd{mkHref ? ', ook op ' : ' via kleda.nl'}
          {mkHref ? <a href={mkHref} className="underline">Mijn Kleda</a> : null}.
        </p>
      </Card>
    )
  }

  // ── Niets te kiezen ───────────────────────────────────────────
  const ritmesMetStart = INTERVALLEN.filter((m) => (ritmeOpties[String(m)] ?? []).length > 0)
  const kanKiezen = ABONNEMENT_AAN && toegestaan && (ritmesMetStart.length > 0 || heeftSeizoenen)
  if (!kanKiezen) {
    if (!standalone) return null
    return (
      <Card className={className}>
        <p className="text-body font-medium text-ink mb-xs">Volgende reiniging</p>
        <p className="text-body-sm text-ink-muted">
          {leegTekst ?? (!toegestaan
            ? 'Je kunt een kleed-abonnement nemen zodra je afleverafspraak staat.'
            : 'Voor deze bestelling is het kleed-abonnement niet meer te kiezen.')}
          {mkHref ? <> Op <a href={mkHref} className="underline">Mijn Kleda</a> zie je je kleden en kun je opnieuw bestellen.</> : null}
        </p>
      </Card>
    )
  }

  const ritmeKeuzes = interval ? (ritmeOpties[String(interval)] ?? []) : []
  const kanaalVoorkeur = KANAALKEUZE_AAN && heeftMobiel ? kanaal : undefined

  const bevestig = async () => {
    if (!interval) return
    setBezig(true)
    setFout(null)
    const r = await maakKleedAbonnement({ token, intervalMaanden: interval, seizoen: startKeuze ?? undefined, kanaalVoorkeur, bron })
    setBezig(false)
    if (!r.success || !r.data) {
      setFout(r.error?.message || 'Dat lukte niet. Probeer het zo opnieuw of bel ons op 088 299 9277.')
      return
    }
    trackAbonnementConfirmed(werkOrderId, interval, startKeuze ?? '', bron)
    // overslaanTot komt van de server (zelfde regel als na een herlaad); de berekening hier is alleen de terugval.
    const next = r.data.nextRunAt
    const [y, m, d] = next.split('-').map(Number)
    const tot = r.data.overslaanTot ?? new Date(Date.UTC(y, m - 1, d - 11)).toISOString().slice(0, 10)
    setAbonnement({
      id: r.data.id, intervalMaanden: interval, nextRunAt: next, seizoenLabel: r.data.seizoen,
      overslaanMag: true, overslaanTot: tot, kortingPct: r.data.kortingPct ?? 10,
    })
    setNetBevestigd(true)
  }

  const zelfBeslissen = () => {
    setZelf(true)
    trackTerugkomDeclined(werkOrderId, bron)
    void zetTerugkom({ token, actie: 'zelf', bron })
  }

  const chip = (actief: boolean) =>
    `rounded-xl border px-sm py-sm text-body-sm font-medium transition-colors ${actief ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink hover:border-ink'}`

  // ── De vraag ──────────────────────────────────────────────────
  return (
    <Card className={className}>
      <p className="text-body font-medium text-ink mb-xs">Neem een kleed-abonnement</p>
      <p className="text-body-sm text-ink-muted mb-md">
        Wij reinigen je kleed op een vast ritme, met 10% korting op elke reiniging. Jij kiest hoe vaak.
      </p>
      <div className="grid grid-cols-2 gap-xs sm:grid-cols-4">
        {INTERVALLEN.map((m) => (
          <button key={m} type="button" onClick={() => kiesInterval(m)} aria-pressed={interval === m} className={chip(interval === m)}>
            {ritmeKnop(m)}
          </button>
        ))}
      </div>
      {interval && (
        <div className="mt-sm">
          <p className="text-body-sm text-ink-muted">
            {startKeuze
              ? <>Eerste reiniging {inSeizoen(startKeuze)}, daarna {ritmeZin(interval)}.</>
              : <>Eerste reiniging over {interval} maanden, daarna {ritmeZin(interval)}.</>}
            {ritmeKeuzes.length > 1 && !seizoenOpen && (
              <>{' '}<button type="button" onClick={() => setSeizoenOpen(true)} className="underline">Ander seizoen?</button></>
            )}
          </p>
          {seizoenOpen && ritmeKeuzes.length > 1 && (
            <div className="mt-xs flex flex-wrap gap-xs">
              {ritmeKeuzes.map((o) => (
                <button key={o.label} type="button" onClick={() => setStartKeuze(o.label)} aria-pressed={startKeuze === o.label}
                  className={`rounded-xl border px-sm py-xs text-body-sm font-medium ${startKeuze === o.label ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink hover:border-ink'}`}>
                  {kapitaal(o.naam)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {KANAALKEUZE_AAN && heeftMobiel && interval && (
        <div className="mt-sm">
          <p className="text-body-sm text-ink-muted mb-xs">Hoe wil je het bericht vooraf ontvangen?</p>
          <div className="flex gap-xs">
            {(['mail', 'whatsapp'] as const).map((k) => (
              <button key={k} type="button" onClick={() => setKanaal(k)}
                className={`rounded-xl border px-sm py-xs text-body-sm ${kanaal === k ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink'}`}>
                {k === 'mail' ? 'Mail' : 'WhatsApp'}
              </button>
            ))}
          </div>
        </div>
      )}
      <p className="mt-sm text-body-sm text-ink-muted">
        Drie weken vooraf krijg je bericht; dan kun je een keer overslaan of stoppen. 10% korting op reiniging en behandelingen, tegen de prijs van dat moment. Betalen per reiniging, stoppen kan altijd.{' '}
        <a href={VOORWAARDEN} target="_blank" rel="noopener" className="underline">Voorwaarden</a>
      </p>
      <div className="mt-md flex flex-wrap items-center gap-sm">
        <button type="button" disabled={!interval || bezig} onClick={() => void bevestig()}
          className="rounded-xl bg-ink px-md py-sm text-body-sm font-semibold text-white disabled:opacity-40">
          Zet mijn abonnement aan
        </button>
        <button type="button" disabled={bezig} onClick={zelfBeslissen} className="text-body-sm text-ink-muted underline">
          Ik beslis zelf
        </button>
      </div>
      {fout && <p className="mt-sm text-body-sm text-red-700">{fout}</p>}
    </Card>
  )
}
